import { useEffect, useState } from "react";
import { useApi } from "../lib/api";
import { useI18n } from "../i18n/I18nProvider";
import { useDotBotsAuth } from "@dotbots-boutique/auth-sdk/react";
import { hasPermission } from "../lib/permissions";
import { confirmDialog, notifyDialog } from "../components/shared/modals";

type RuleKind = "from_domain" | "from_address" | "subject_contains";

interface MatchRule {
  id: string;
  kind: RuleKind;
  pattern: string;
  note: string | null;
  created_at: string;
  updated_at: string;
}

interface ApplyResult { matched: number; closed: number; scanned: number }

const EMPTY_DRAFT = { kind: "from_domain" as RuleKind, pattern: "", note: "", applyToInbox: true };

// Admin screen for the "auto-archive on inbound match" rules.
// Each row is a (kind, pattern) pair the backend checks against
// every new inbound mail; on a hit the ticket is created and
// immediately moved to 'closed' with an automation-log entry.
export default function AutoArchiveRules() {
  const { t } = useI18n();
  const api = useApi();
  const { user } = useDotBotsAuth();
  const roles = user?.roles ?? [];
  const canManage = hasPermission(roles, "agents.write");

  const [rows, setRows] = useState<MatchRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Per-row apply state: 'running' while the backend is scanning, or
  // the final sweep result (shown next to the "Apply" button for a few
  // seconds so the operator can see "closed X of Y").
  const [applyState, setApplyState] = useState<Record<string, "running" | ApplyResult>>({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get<{ data: MatchRule[] }>("/api/match-rules");
      setRows(res.data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (canManage) void load(); }, [canManage]);

  const createOne = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.pattern.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api.post<{ data: MatchRule; sweep?: ApplyResult }>("/api/match-rules", {
        kind: draft.kind,
        pattern: draft.pattern.trim(),
        note: draft.note.trim() || undefined,
        applyToInbox: draft.applyToInbox,
      });
      setDraft(EMPTY_DRAFT);
      await load();
      if (res.sweep) {
        void notifyDialog({
          message: t("autoArchive.sweepResult", {
            closed: String(res.sweep.closed),
            scanned: String(res.sweep.scanned),
          }),
        });
      }
    } catch (err) {
      setError(describeError(err, t));
    } finally {
      setSaving(false);
    }
  };

  const applyOne = async (row: MatchRule) => {
    setApplyState((prev) => ({ ...prev, [row.id]: "running" }));
    try {
      const res = await api.post<{ data: ApplyResult }>(`/api/match-rules/${row.id}/apply`);
      setApplyState((prev) => ({ ...prev, [row.id]: res.data }));
    } catch (err) {
      setApplyState((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
      void notifyDialog({ message: (err as Error).message });
    }
  };

  const deleteOne = async (row: MatchRule) => {
    const ok = await confirmDialog({
      title: t("autoArchive.deleteConfirmTitle"),
      message: t("autoArchive.deleteConfirmBody", { pattern: row.pattern }),
      confirmLabel: t("common.delete"),
      cancelLabel: t("common.cancel"),
    });
    if (!ok) return;
    try {
      await api.del(`/api/match-rules/${row.id}`);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (err) {
      void notifyDialog({ message: (err as Error).message });
    }
  };

  if (!canManage) {
    return (
      <div className="page">
        <h2>{t("autoArchive.title")}</h2>
        <p>{t("autoArchive.noAccess")}</p>
      </div>
    );
  }

  return (
    <div className="page">
      <h2>{t("autoArchive.title")}</h2>
      <p style={{ color: "var(--text-muted)", maxWidth: 720 }}>{t("autoArchive.intro")}</p>

      <form className="card" onSubmit={createOne} style={{ marginTop: 16, maxWidth: 820 }}>
        <h3 style={{ marginTop: 0 }}>{t("autoArchive.addTitle")}</h3>
        <div className="form-grid form-grid-2">
          <div className="field">
            <label>{t("autoArchive.kindLabel")}</label>
            <select value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value as RuleKind })}>
              <option value="from_domain">{t("autoArchive.kind.from_domain")}</option>
              <option value="from_address">{t("autoArchive.kind.from_address")}</option>
              <option value="subject_contains">{t("autoArchive.kind.subject_contains")}</option>
            </select>
            <small style={{ color: "var(--text-muted)", display: "block", marginTop: 4 }}>
              {kindHelp(draft.kind, t)}
            </small>
          </div>
          <div className="field">
            <label>{t("autoArchive.patternLabel")}</label>
            <input
              value={draft.pattern}
              onChange={(e) => setDraft({ ...draft, pattern: e.target.value })}
              placeholder={kindPlaceholder(draft.kind)}
              required
            />
          </div>
        </div>
        <div className="field">
          <label>{t("autoArchive.noteLabel")}</label>
          <input
            value={draft.note}
            onChange={(e) => setDraft({ ...draft, note: e.target.value })}
            placeholder={t("autoArchive.notePlaceholder")}
          />
        </div>
        <div className="field">
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={draft.applyToInbox}
              onChange={(e) => setDraft({ ...draft, applyToInbox: e.target.checked })}
            />
            <span>{t("autoArchive.applyToInbox")}</span>
          </label>
          <small style={{ color: "var(--text-muted)", display: "block", marginTop: 4 }}>
            {t("autoArchive.applyToInboxHelp")}
          </small>
        </div>
        {error && <div style={{ color: "var(--danger)", marginBottom: 8 }}>{error}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button type="submit" className="primary" disabled={saving || !draft.pattern.trim()}>
            {saving ? t("common.saving") : t("autoArchive.addButton")}
          </button>
        </div>
      </form>

      <div className="card" style={{ marginTop: 16, maxWidth: 820 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3 style={{ margin: 0 }}>{t("autoArchive.listTitle")}</h3>
          <button type="button" onClick={() => void load()} disabled={loading}>
            {loading ? t("common.loading") : t("common.refresh")}
          </button>
        </div>
        {rows.length === 0 ? (
          <p style={{ color: "var(--text-muted)", marginTop: 8 }}>{t("autoArchive.empty")}</p>
        ) : (
          <div style={{ overflowX: "auto", marginTop: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", borderBottom: "1px solid var(--border)", padding: 6 }}>{t("autoArchive.colKind")}</th>
                  <th style={{ textAlign: "left", borderBottom: "1px solid var(--border)", padding: 6 }}>{t("autoArchive.colPattern")}</th>
                  <th style={{ textAlign: "left", borderBottom: "1px solid var(--border)", padding: 6 }}>{t("autoArchive.colNote")}</th>
                  <th style={{ borderBottom: "1px solid var(--border)", padding: 6 }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const state = applyState[r.id];
                  return (
                    <tr key={r.id}>
                      <td style={{ padding: 6, whiteSpace: "nowrap" }}>{t(`autoArchive.kind.${r.kind}`)}</td>
                      <td style={{ padding: 6, fontFamily: "monospace" }}>{r.pattern}</td>
                      <td style={{ padding: 6, color: "var(--text-muted)" }}>{r.note ?? "—"}</td>
                      <td style={{ padding: 6, textAlign: "right", whiteSpace: "nowrap" }}>
                        {state && state !== "running" && (
                          <span style={{ marginRight: 8, color: "var(--text-muted)", fontSize: 12 }}>
                            {t("autoArchive.sweepRowResult", {
                              closed: String(state.closed),
                              scanned: String(state.scanned),
                            })}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => void applyOne(r)}
                          disabled={state === "running"}
                          style={{ marginRight: 6 }}
                        >
                          {state === "running" ? t("autoArchive.applying") : t("autoArchive.applyRow")}
                        </button>
                        <button type="button" onClick={() => void deleteOne(r)}>{t("common.delete")}</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function kindHelp(kind: RuleKind, t: (k: string) => string): string {
  return t(`autoArchive.help.${kind}`);
}
function kindPlaceholder(kind: RuleKind): string {
  if (kind === "from_domain") return "mail.instagram.com";
  if (kind === "from_address") return "noreply@stripe.com";
  return "Your receipt";
}

// Translate "API 409: {..."RULE_EXISTS"...}" into a friendly message.
// Other errors fall through unchanged so build/network failures stay
// visible for debugging.
function describeError(err: unknown, t: (k: string) => string): string {
  const msg = (err as Error).message ?? String(err);
  if (msg.includes("409") && msg.includes("RULE_EXISTS")) {
    return t("autoArchive.duplicateError");
  }
  return msg;
}
